/**
 * Rendu figé de SA PROPRE retouche.
 *
 * Sert au dessinateur pendant la phase de devinette du mode « un seul dessine » :
 * il n'a rien a chercher, mais regarder les autres traquer une retouche qu'on ne
 * voit plus est frustrant. On la lui remontre, sans aucun outil.
 *
 * Passe par `prepareArtwork`, le meme chemin de rendu que la galerie et la
 * revelation : ce qu'il voit est exactement ce que les autres verront.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import type { DrawZone } from '@retusch/shared';
import { prepareArtwork } from '../gallery/renderArtwork.js';

const SIZE = 320;

export function OwnRetouch({
  file,
  zone,
  palette,
  drawing,
  size = SIZE,
}: {
  file: string;
  zone: DrawZone;
  palette: readonly string[];
  drawing: ArrayBuffer;
  /** Le mode spectateur reutilise ce rendu, en plus grand : c'est son ecran. */
  size?: number;
}): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas === null) return;
    let alive = true;

    void prepareArtwork({ file, zone, palette, drawing })
      .then((prepared) => {
        if (!alive) return;
        const pixels = Math.round(size * Math.min(window.devicePixelRatio || 1, 2));
        canvas.style.width = `${size}px`;
        canvas.style.height = `${size}px`;
        prepared.paint(canvas, { size: pixels, badge: true });
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [file, zone, palette, drawing, size]);

  return <canvas ref={canvasRef} className="own-retouch" />;
}
